import { Button, HStack, Stack, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FullModal } from '$/components/common/Modals/Modal';
import {
  HookFormInput,
  type HookFormInputProps,
} from '$/components/core/Form/HookFormInput';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import { inviteTeacher } from '$/services/usecases/school/schools';
import { emailFormatRegex } from '$/utils/regexUtils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  schoolId: string;
}

type InviteTeacherFormData = {
  email: string;
  firstName: string;
  lastName: string;
};

const Input = (props: HookFormInputProps<InviteTeacherFormData>) => (
  <HookFormInput<InviteTeacherFormData> {...props} />
);

export const InviteTeacherModal: FC<Props> = ({
  isOpen,
  onClose,
  schoolId,
}) => {
  const { t } = useTranslation();
  const { cloudMessageTranslation } = useCloudMessageTranslation();
  const toast = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: inviteTeacher,
    onSuccess: (data) => {
      if (!data.isSuccessful) {
        toast(cloudMessageTranslation(data.response.message), 'error', '');
        return;
      }

      toast(
        t('school.teachers.inviteModal.toastTitle'),
        'success',
        t('school.teachers.inviteModal.toastDescription'),
      );

      onCloseModal();
    },
  });

  const formMethods = useForm<InviteTeacherFormData>({
    mode: 'onTouched',
  });

  const onSubmit = (data: InviteTeacherFormData) => {
    mutate({ schoolId, ...data });
  };

  const onCloseModal = () => {
    onClose();
    formMethods.reset();
  };

  return (
    <FullModal
      isOpen={isOpen}
      onClose={onCloseModal}
      header={t('school.teachers.inviteModal.title')}
      size='xl'
      isScrollable={false}
    >
      <FormProvider {...formMethods}>
        <Stack as='form' gap='4' onSubmit={formMethods.handleSubmit(onSubmit)}>
          <HStack w='full'>
            <Input
              accessor='firstName'
              label={t('dashboard.authentication.firstNameLabel')}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
            />
            <Input
              accessor='lastName'
              label={t('dashboard.authentication.lastNameLabel')}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
            />
          </HStack>
          <Input
            accessor='email'
            label={t('dashboard.authentication.emailLabel')}
            registerOptions={{
              required: t('dashboard.authentication.errorMessages.isRequired'),
              pattern: {
                value: emailFormatRegex,
                message: t(
                  'dashboard.authentication.errorMessages.wrongFormat',
                ),
              },
            }}
          />
          <Text color='lighterText' fontSize='sm'>
            {t('school.teachers.inviteModal.expiryInfo')}
          </Text>
          <HStack justify='space-between' w='full' mt='10'>
            <Button
              isDisabled={isPending}
              onClick={onCloseModal}
              type='button'
              variant='ghost'
            >
              {t('general.cancel')}
            </Button>
            <Button
              isDisabled={!formMethods.formState.isValid}
              isLoading={isPending}
              type='submit'
            >
              {t('school.teachers.inviteModal.inviteButton')}
            </Button>
          </HStack>
        </Stack>
      </FormProvider>
    </FullModal>
  );
};
